import { Children, Member, SocialBenefit, SocialBenefitListItem } from '@services/member/member/member.types';
import { useParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { useGetSocialBenefitsMutation } from '@services/member/list/social-benefits/social-benefits.endpoints';
import {
    useAddMemberSocialBenefitMutation,
    useArchiveMemberSocialBenefitMutation,
    useGetMemberArchivedSocialBenefitsMutation,
    useGetMemberMutation,
    useGetMemberSocialBenefitsMutation,
    useUpdateMemberSocialBenefitMutation
} from '@services/member/member/member.endpoints';
import { useState, useMemo, useEffect } from 'react';
import { PaginationMetadata } from '@services/providers/cosem.types';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

export const useDetailsSocialBenefitsVM = () => {
    const [socialBenefits, setSocialBenefits] = useState<SocialBenefitListItem[]>([]);
    const [totalSocialBenefits, setTotalSocialBenefits] = useState<number>(0);
    const [isArchived, setIsArchived] = useState<boolean>(false);
    const [needReload, setNeedReload] = useState<boolean>();
    const MySwal = withReactContent(Swal);
    const { id } = useParams();
    const socialBenefitColumnHelper = createColumnHelper<SocialBenefit>();
    const [childrens, setChildrens] = useState<Children[]>([]);
    const [member, setMember] = useState<Member>();

    const [
        getMember,
        {
            isLoading: isGetMemberLoading,
            isError: isGetMemberError,
            isSuccess: isGetMemberSuccess,
            error: getMemberError,
            data: getMemberResponse
        }
    ] = useGetMemberMutation();

    const [
        getSocialBenefits,
        {
            isLoading: isGetSocialBenefitsLoading,
            isSuccess: isGetSocialBenefitsSuccess,
            isError: isGetSocialBenefitsError,
            error: getSocialBenefitsError,
            data: getSocialBenefitsData
        }
    ] = useGetSocialBenefitsMutation();

    const [
        updateMemberSocialBenefit,
        {
            isLoading: isUpdateMemberSocialBenefitLoading,
            isSuccess: isUpdateMemberSocialBenefitSuccess,
            isError: isUpdateMemberSocialBenefitError,
            error: updateMemberSocialBenefitError,
            data: updateMemberSocialBenefitData
        }
    ] = useUpdateMemberSocialBenefitMutation();

    const [
        addMemberSocialBenefit,
        {
            isLoading: isAddMemberSocialBenefitLoading,
            isSuccess: isAddMemberSocialBenefitSuccess,
            isError: isAddMemberSocialBenefitError,
            error: addMemberSocialBenefitError,
            data: addMemberSocialBenefitData
        }
    ] = useAddMemberSocialBenefitMutation();

    const [
        archiveMemberSocialBenefit,
        {
            isLoading: isArchiveMemberSocialBenefitLoading,
            isSuccess: isArchiveMemberSocialBenefitSuccess,
            isError: isArchiveMemberSocialBenefitError,
            error: archiveMemberSocialBenefitError,
            data: archiveMemberSocialBenefitData
        }
    ] = useArchiveMemberSocialBenefitMutation();

    const handleArchiveSocialBenefits = async (lines: SocialBenefit[]) => {
        MySwal.fire({
            title:
                lines.length === 1
                    ? 'Êtes-vous sûr de vouloir archiver cette prestation sociale ?'
                    : 'Êtes-vous sûr de vouloir archiver ces prestations sociales ?',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Archiver',
            cancelButtonColor: '#FF5F46',
            cancelButtonText: 'Annuler'
        }).then(async (result) => {
            if (result.isConfirmed) {
                for (const line of lines) {
                    await archiveMemberSocialBenefit({ id: String(id), socialBenefitId: line.id });
                }
            }
        });
    };

    useEffect(() => {
        getSocialBenefits();
    }, []);

    useEffect(() => {
        if (!id) return;
        getMember({ id });
    }, [id]);

    useEffect(() => {
        if (isGetMemberLoading) return;
        if (isGetMemberError) return;
        if (getMemberError) return;

        if (isGetMemberSuccess && getMemberResponse) {
            setChildrens(getMemberResponse.data.children);
            setMember(getMemberResponse.data);
        }
    }, [isGetMemberError, isGetMemberSuccess, getMemberError, getMemberResponse, isGetMemberLoading]);

    useEffect(() => {
        if (isAddMemberSocialBenefitSuccess || isUpdateMemberSocialBenefitSuccess || isArchiveMemberSocialBenefitSuccess) {
            setNeedReload((prev) => !prev);
        }
    }, [isAddMemberSocialBenefitSuccess, isUpdateMemberSocialBenefitSuccess, isArchiveMemberSocialBenefitSuccess]);

    useEffect(() => {
        if (isUpdateMemberSocialBenefitLoading) return;
        if (isUpdateMemberSocialBenefitError && updateMemberSocialBenefitError) {
            return;
        }
        if (isUpdateMemberSocialBenefitSuccess && updateMemberSocialBenefitData) {
            return;
        }
    }, [
        isUpdateMemberSocialBenefitLoading,
        isUpdateMemberSocialBenefitSuccess,
        isUpdateMemberSocialBenefitError,
        updateMemberSocialBenefitData,
        updateMemberSocialBenefitError
    ]);

    useEffect(() => {
        if (isAddMemberSocialBenefitLoading) return;
        if (isAddMemberSocialBenefitError && addMemberSocialBenefitError) {
            return;
        }
        if (isAddMemberSocialBenefitSuccess && addMemberSocialBenefitData) {
            return;
        }
    }, [
        isAddMemberSocialBenefitLoading,
        isAddMemberSocialBenefitSuccess,
        isAddMemberSocialBenefitError,
        addMemberSocialBenefitData,
        addMemberSocialBenefitError
    ]);

    useEffect(() => {
        if (isArchiveMemberSocialBenefitLoading) return;
        if (isArchiveMemberSocialBenefitError && archiveMemberSocialBenefitError) {
            return;
        }
        if (isArchiveMemberSocialBenefitSuccess && archiveMemberSocialBenefitData) {
            return;
        }
    }, [
        isArchiveMemberSocialBenefitLoading,
        isArchiveMemberSocialBenefitSuccess,
        isArchiveMemberSocialBenefitError,
        archiveMemberSocialBenefitData,
        archiveMemberSocialBenefitError
    ]);

    const mutationHook = useMemo(() => {
        return isArchived ? useGetMemberArchivedSocialBenefitsMutation : useGetMemberSocialBenefitsMutation;
    }, [isArchived]);

    useEffect(() => {
        setTotalSocialBenefits(0);
    }, [isArchived]);

    const handleMetadata = (metadata: PaginationMetadata) => {
        if (metadata && metadata.total !== undefined) {
            setTotalSocialBenefits(metadata.total);
        }
    };

    useEffect(() => {
        if (isGetSocialBenefitsLoading) return;
        if (isGetSocialBenefitsError && getSocialBenefitsError) {
            return;
        }
        if (isGetSocialBenefitsSuccess && getSocialBenefitsData) {
            setSocialBenefits([...getSocialBenefitsData.data].sort((a, b) => a.label.localeCompare(b.label)));
            return;
        }
    }, [isGetSocialBenefitsSuccess, getSocialBenefitsData]);

    return {
        mutationHook,
        totalSocialBenefits,
        setTotalSocialBenefits,
        isArchived,
        handleMetadata,
        handleArchiveSocialBenefits,
        needReload,
        MySwal,
        updateMemberSocialBenefit,
        addMemberSocialBenefit,
        setIsArchived,
        id,
        socialBenefitColumnHelper,
        socialBenefits,
        childrens,
        member
    };
};
